import moment, { Moment } from 'moment';
import { PlatformEphemerisCurrentActivationDto } from '@data/_generated-api/platform-api/models/platform-ephemeris-current-activation-dto';
import { PlatformEphemerisCurrentGateDto } from '@data/_generated-api/platform-api/models/platform-ephemeris-current-gate-dto';
import { PlatformEphemerisGateTransitionDto } from '@data/_generated-api/platform-api/models/platform-ephemeris-gate-transition-dto';
import { PlatformEphemerisLineTransitionDto } from '@data/_generated-api/platform-api/models/platform-ephemeris-line-transition-dto';
import { PlatformEphemerisNowNextPlanetDto } from '@data/_generated-api/platform-api/models/platform-ephemeris-now-next-planet-dto';
import { PlatformEphemerisPlanetSegmentsDto } from '@data/_generated-api/platform-api/models/platform-ephemeris-planet-segments-dto';
import { PlatformEphemerisPositionDto } from '@data/_generated-api/platform-api/models/platform-ephemeris-position-dto';
import { PlatformEphemerisSegmentDto } from '@data/_generated-api/platform-api/models/platform-ephemeris-segment-dto';
import { PlatformEphemerisStationDto } from '@data/_generated-api/platform-api/models/platform-ephemeris-station-dto';
import {
  getHdPlanetDisplayConfig,
  getHdPlanetDisplayConfigByPlanetId,
  HdPlanetKey,
} from '../chart/bodygraph/planet-grid/hd-planet-display-config';
import {
  HdTransitRetrogradeSpan,
  buildRetrogradeSpans,
  resolveRetrogradeAtWindowStart,
} from './transit-retrograde.utils';

/**
 * Domeinmodellen voor de transits-feature (fase F1: data-laag).
 *
 * Tijdsconventie: alle `Moment`-instanties in dit bestand zijn **UTC**
 * (`moment.utc(...)`). De API levert ISO-8601 UTC-strings; conversie naar de
 * tijdzone van de kijker is een weergave-aangelegenheid van de componenten.
 *
 * De gegenereerde DTO's hebben uitsluitend optionele/nullable props
 * (repo-regel) — de converters hier zijn defensief: incomplete entries
 * leveren `null` op en worden door de service overgeslagen i.p.v. een
 * halve state te publiceren.
 */

// ── Types ────────────────────────────────────────────────────────────────

/** Granulariteit van segmenten: alleen poort-overgangen, of poort+lijn. */
export type TransitGranularity = 'gate' | 'line';

/**
 * Poortinfo bij de wisselblokken van de verticale dag-flow: enkel de poortlink,
 * een badge met de twee keynotes, of een extra rij met de keynotes en de korte
 * I Tjing-beschrijving (setting `transitsGateInfoMode`).
 */
export type HdTransitGateInfoMode = 'link' | 'badge' | 'row';

/** Richting waarnaar een hemellichaam keert op een station. */
export type HdTransitStationDirection = 'retrograde' | 'direct';

/** Eén transit-segment: één poort (en bij line-granulariteit één lijn). */
export interface HdTransitSegment {
  readonly gate: number;
  /** Lijn 1-6; `null` bij gate-granulariteit. */
  readonly line: number | null;
  /** Echte start (UTC) — kan vóór het gevraagde venster liggen. */
  readonly fromUtc: Moment;
  /** Echte (exclusieve) eindtijd (UTC) — kan na het venster liggen. */
  readonly toUtc: Moment;
  /**
   * true als het hemellichaam op het MIDDEN van het segment retrograde loopt. Een
   * segment kan een station bevatten: voor de richting op een moment
   * `HdTransitPlanetSegments.retrogradeSpans` gebruiken (zie transit-retrograde.utils).
   */
  readonly retrograde: boolean;
}

/** Station (stilstandpunt): de snelheid gaat door nul, met de nieuwe richting. */
export interface HdTransitStation {
  readonly atUtc: Moment;
  readonly turnsTo: HdTransitStationDirection;
}

/** Segmenten + stations van één planeet over een venster. */
export interface HdTransitPlanetSegments {
  readonly planetId: number;
  readonly sourceKey: string;
  /** null als de (onverwachte) sourceKey/planetId niet in de Maia-mapping zit. */
  readonly planetKey: HdPlanetKey | null;
  readonly segments: readonly HdTransitSegment[];
  readonly stations: readonly HdTransitStation[];
  /**
   * Exacte retrograde-perioden binnen het geladen venster (samengevoegd,
   * oplopend), van station tot station. Buiten het venster: onbekend (leeg).
   */
  readonly retrogradeSpans: readonly HdTransitRetrogradeSpan[];
}

/** Een lijn-overgang: `atUtc` = echte start van dat lijn-segment. */
export interface HdTransitLineTransition {
  readonly atUtc: Moment;
  readonly gate: number;
  readonly line: number;
}

/** Een poort-overgang: `atUtc` = echte start van dat poort-segment. */
export interface HdTransitGateTransition {
  readonly atUtc: Moment;
  readonly gate: number;
}

/** Huidige stand van één planeet met de omliggende lijn- en poort-overgangen. */
export interface HdTransitNowNextPlanet {
  readonly planetId: number;
  readonly sourceKey: string;
  readonly planetKey: HdPlanetKey | null;
  /** true als de snelheid in ecliptische lengte negatief is. */
  readonly retrograde: boolean;
  /** Het lijn-segment waarin de planeet staat (sinceUtc = echte start). */
  readonly current: {
    readonly gate: number;
    readonly line: number;
    readonly sinceUtc: Moment;
  };
  /** Het poort-segment waarin de planeet staat (sinceUtc = echte start). */
  readonly currentGate: {
    readonly gate: number;
    readonly sinceUtc: Moment;
  } | null;
  readonly prevLine: HdTransitLineTransition | null;
  readonly nextLine: HdTransitLineTransition | null;
  readonly prevGate: HdTransitGateTransition | null;
  readonly nextGate: HdTransitGateTransition | null;
}

/** Ruwe positie + HD-activatie van één planeet op één moment. */
export interface HdTransitPosition {
  readonly planetId: number;
  readonly sourceKey: string;
  readonly planetKey: HdPlanetKey | null;
  /** Ecliptische lengtegraad in graden [0, 360). */
  readonly longitude: number;
  /** Snelheid van de lengtegraad in graden per dag (negatief = retrograde). */
  readonly speedLongitude: number;
  readonly retrograde: boolean;
  readonly gate: number;
  readonly line: number;
}

// ── sourceKey ↔ planetKey ↔ planetId ─────────────────────────────────────

/**
 * Vaste mapping planetKey → kebab-case sourceKey.
 * Geverifieerd tegen de backend `MaiaPlanetMapping.cs`
 * (`Egitura.Platform.Domain.HumanDesign/Mapping`) — enkel northNode/southNode
 * wijken af van plain lowercase.
 */
const HD_TRANSIT_SOURCE_KEYS: Readonly<Record<HdPlanetKey, string>> = {
  sun: 'sun',
  earth: 'earth',
  moon: 'moon',
  northNode: 'north-node',
  southNode: 'south-node',
  mercury: 'mercury',
  venus: 'venus',
  mars: 'mars',
  jupiter: 'jupiter',
  saturn: 'saturn',
  uranus: 'uranus',
  neptune: 'neptune',
  pluto: 'pluto',
  chiron: 'chiron',
};

/** Lowercase-sourceKey → planetKey, voor defensieve (case-insensitieve) parsing. */
const HD_TRANSIT_PLANET_KEYS_BY_SOURCE_KEY: ReadonlyMap<string, HdPlanetKey> = new Map(
  (Object.entries(HD_TRANSIT_SOURCE_KEYS) as [HdPlanetKey, string][]).map(([key, sourceKey]) => [sourceKey, key]),
);

/** sourceKey voor een planetKey, bv. `'northNode'` → `'north-node'`. */
export function getTransitSourceKey(planetKey: HdPlanetKey): string {
  return HD_TRANSIT_SOURCE_KEYS[planetKey];
}

/** Komma-gescheiden sourceKeys voor de `planets`-queryparam, in invoervolgorde. */
export function getTransitPlanetsParam(planetKeys: readonly HdPlanetKey[]): string {
  return planetKeys.map(getTransitSourceKey).join(',');
}

/** planetKey voor een sourceKey (case-insensitief), `null` bij een onbekende key. */
export function getTransitPlanetKeyBySourceKey(sourceKey: string | null | undefined): HdPlanetKey | null {
  if (!sourceKey) return null;
  return HD_TRANSIT_PLANET_KEYS_BY_SOURCE_KEY.get(sourceKey.toLowerCase()) ?? null;
}

/** planetKey voor een Maia planetId (0-13), `null` bij een onbekend id. */
export function getTransitPlanetKeyByPlanetId(planetId: number | null | undefined): HdPlanetKey | null {
  if (planetId == null) return null;
  return getHdPlanetDisplayConfigByPlanetId(planetId)?.key ?? null;
}

/** Maia planetId voor een planetKey. */
export function getTransitPlanetId(planetKey: HdPlanetKey): number {
  return getHdPlanetDisplayConfig(planetKey).planetId;
}

// ── Defensieve DTO → domein converters ───────────────────────────────────

/** Parse een ISO-8601 UTC-string naar een UTC-Moment; `null` als ongeldig. */
function parseUtc(iso: string | null | undefined): Moment | null {
  if (!iso) return null;
  const parsed = moment.utc(iso, moment.ISO_8601, true);
  return parsed.isValid() ? parsed : null;
}

/** Converteert één segment-DTO; `null` als gate/fromUtc/toUtc ontbreekt of ongeldig is. */
export function toHdTransitSegment(dto: PlatformEphemerisSegmentDto): HdTransitSegment | null {
  const fromUtc = parseUtc(dto.fromUtc);
  const toUtc = parseUtc(dto.toUtc);
  if (dto.gate == null || fromUtc == null || toUtc == null) return null;
  return {
    gate: dto.gate,
    line: dto.line ?? null,
    fromUtc,
    toUtc,
    retrograde: dto.retrograde === true,
  };
}

/**
 * Converteert één station-DTO; `null` als atUtc/turnsTo ontbreekt of ongeldig is.
 * `turnsTo` is runtime lowercase ("retrograde"/"direct") ondanks PascalCase in de
 * swagger-enum — daarom case-insensitief vergeleken.
 */
export function toHdTransitStation(dto: PlatformEphemerisStationDto): HdTransitStation | null {
  const atUtc = parseUtc(dto.atUtc);
  const turnsTo = (dto.turnsTo as string | undefined)?.toLowerCase();
  if (atUtc == null || (turnsTo !== 'retrograde' && turnsTo !== 'direct')) return null;
  return { atUtc, turnsTo };
}

/**
 * Converteert de segmenten/stations van één planeet; `null` als planetId ontbreekt.
 * Individuele incomplete segmenten/stations worden overgeslagen. `window` is het
 * gevraagde venster: daarover worden de exacte retrograde-perioden opgebouwd
 * (richting op het vensterbegin + de stations erin).
 */
export function toHdTransitPlanetSegments(
  dto: PlatformEphemerisPlanetSegmentsDto,
  window: { readonly fromMs: number; readonly toMs: number },
): HdTransitPlanetSegments | null {
  if (dto.planetId == null) return null;
  const segments = (dto.segments ?? [])
    .map(toHdTransitSegment)
    .filter((segment): segment is HdTransitSegment => segment != null);
  const stations = (dto.stations ?? [])
    .map(toHdTransitStation)
    .filter((station): station is HdTransitStation => station != null)
    .sort((a, b) => a.atUtc.valueOf() - b.atUtc.valueOf());
  const retrogradeAtStart = resolveRetrogradeAtWindowStart(
    dto.retrogradeAtFromUtc,
    stations,
    segments,
    window.fromMs,
    window.toMs,
  );
  return {
    planetId: dto.planetId,
    sourceKey: dto.sourceKey ?? '',
    planetKey: getTransitPlanetKeyByPlanetId(dto.planetId),
    segments,
    stations,
    retrogradeSpans: buildRetrogradeSpans(retrogradeAtStart, stations, window.fromMs, window.toMs),
  };
}

function toHdTransitLineTransition(dto: PlatformEphemerisLineTransitionDto | null | undefined): HdTransitLineTransition | null {
  if (dto == null) return null;
  const atUtc = parseUtc(dto.atUtc);
  if (atUtc == null || dto.gate == null || dto.line == null) return null;
  return { atUtc, gate: dto.gate, line: dto.line };
}

function toHdTransitGateTransition(dto: PlatformEphemerisGateTransitionDto | null | undefined): HdTransitGateTransition | null {
  if (dto == null) return null;
  const atUtc = parseUtc(dto.atUtc);
  if (atUtc == null || dto.gate == null) return null;
  return { atUtc, gate: dto.gate };
}

function toHdTransitCurrent(dto: PlatformEphemerisCurrentActivationDto | null | undefined): HdTransitNowNextPlanet['current'] | null {
  if (dto == null) return null;
  const sinceUtc = parseUtc(dto.sinceUtc);
  if (sinceUtc == null || dto.gate == null || dto.line == null) return null;
  return { gate: dto.gate, line: dto.line, sinceUtc };
}

function toHdTransitCurrentGate(dto: PlatformEphemerisCurrentGateDto | null | undefined): HdTransitNowNextPlanet['currentGate'] {
  if (dto == null) return null;
  const sinceUtc = parseUtc(dto.sinceUtc);
  if (sinceUtc == null || dto.gate == null) return null;
  return { gate: dto.gate, sinceUtc };
}

/**
 * Converteert de now-next-DTO van één planeet; `null` als planetId of de
 * huidige activatie incompleet is (zonder `current` heeft de entry geen nut).
 * Ontbrekende prev/next-overgangen worden `null`, nooit incomplete objecten.
 */
export function toHdTransitNowNextPlanet(dto: PlatformEphemerisNowNextPlanetDto): HdTransitNowNextPlanet | null {
  const current = toHdTransitCurrent(dto.current);
  if (dto.planetId == null || current == null) return null;
  return {
    planetId: dto.planetId,
    sourceKey: dto.sourceKey ?? '',
    planetKey: getTransitPlanetKeyByPlanetId(dto.planetId),
    retrograde: dto.retrograde === true,
    current,
    currentGate: toHdTransitCurrentGate(dto.currentGate),
    prevLine: toHdTransitLineTransition(dto.prevLine),
    nextLine: toHdTransitLineTransition(dto.nextLine),
    prevGate: toHdTransitGateTransition(dto.prevGate),
    nextGate: toHdTransitGateTransition(dto.nextGate),
  };
}

/** Converteert één positie-DTO; `null` als een van de numerieke velden ontbreekt. */
export function toHdTransitPosition(dto: PlatformEphemerisPositionDto): HdTransitPosition | null {
  if (
    dto.planetId == null ||
    dto.longitude == null ||
    dto.speedLongitude == null ||
    dto.gate == null ||
    dto.line == null
  ) {
    return null;
  }
  return {
    planetId: dto.planetId,
    sourceKey: dto.sourceKey ?? '',
    planetKey: getTransitPlanetKeyByPlanetId(dto.planetId),
    longitude: dto.longitude,
    speedLongitude: dto.speedLongitude,
    retrograde: dto.retrograde === true,
    gate: dto.gate,
    line: dto.line,
  };
}
